import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { map } from "rxjs/operators";
import { AppSettings } from "../../../config/config";
import { apiConstants } from "../../constant/apiConstant";

interface StaffLoginWithPasswordBody {
  mobileOrEmail: string;
  password: string;
  schoolDomain: string;
  partnerDomain?: string;
}

export interface SchoolAndStaffDetail {
  staffId: number;
  staffName: string;
  schoolId: number;
  employmentId: string;
  mobileNumber: string;
  emailId: string;
  des_id: number;
  designation: string;
  isSuperAdmin: number;
  isClassTeacher: number;
  isDriver: number;
  isHostelStaff: number;
  isFinanceAdmin: number;
  isFeeDefaulterAdmin: number;
  schoolName: string;
  schoolImage: string;
  SchoolDomain: string;
  currencyType: string;
  region: string;
  session: string;
  schoolExtensionServicePermissions?: any;
  [key: string]: any;
}

interface StaffLoginWithPasswordResponse {
  status: boolean;
  body: {
    schoolAndStaffDetailData: SchoolAndStaffDetail[];
  };
}

export interface LoginWithQrCodeResponse {
  status: boolean;
  message?: string;
  body?: {
    schoolAndStaffDetailData: SchoolAndStaffDetail[];
  };
}

export interface GenerateQrCodeForStaffLoginResponse {
  status: boolean;
  status_code: number;
  body: {
    code: string;
    // Node's Buffer JSON shape — raw PNG bytes as a plain number array.
    qrCodeImage: {
      type: 'Buffer';
      data: number[];
    };
  };
}

export interface GenerateOtpForForgotPasswordResponse {
  status: boolean;
  message?: string;
  body?: {
    message?: string;
    attemptLeft?: number;
    [key: string]: any;
  };
}

export interface VerifyOtpForForgotPasswordResponse {
  status: boolean;
  message?: string;
  body?: {
    message?: string;
    // One-time token to pass to resetPasswordForStaff, proving the OTP was
    // already verified — the reset call itself doesn't re-check the OTP.
    token?: string;
    [key: string]: any;
  };
}

export interface ResetPasswordForStaffResponse {
  status: boolean;
  message?: string;
  body?: {
    message?: string;
    [key: string]: any;
  };
}

@Injectable()
export class StaffLoginService {
  private url = AppSettings.API_ENDPOINT_STAFF;
  private staffLoginWithPasswordEndpoint = apiConstants.staffLoginWithPassword;
  private generateQrCodeForStaffLoginEndpoint = apiConstants.generateQrCodeForStaffLogin;
  private loginStaffWithQrCodeEndpoint = apiConstants.loginStaffWithQrCode;
  private generateOtpForForgotPasswordEndpoint = apiConstants.generateOtpForForgotPasswordForStaff;
  private verifyOtpForForgotPasswordEndpoint = apiConstants.verifyOtpForForgotPasswordForStaff;
  private resetPasswordForStaffEndpoint = apiConstants.resetPasswordForStaff;

  constructor(private http: HttpClient) {}

  loginWithPassword(body: StaffLoginWithPasswordBody) {
    return this.http
      .post<StaffLoginWithPasswordResponse>(`${this.url}${this.staffLoginWithPasswordEndpoint}`, body)
      .pipe(map((res) => res));
  }

  generateQrCodeForStaffLogin(schoolDomain: string) {
    return this.http
      .post<GenerateQrCodeForStaffLoginResponse>(`${this.url}${this.generateQrCodeForStaffLoginEndpoint}`, { schoolDomain })
      .pipe(map((res) => res));
  }

  loginStaffWithQrCode(code: string, schoolDomain: string) {
    return this.http
      .post<LoginWithQrCodeResponse>(`${this.url}${this.loginStaffWithQrCodeEndpoint}`, { code, schoolDomain })
      .pipe(map((res) => res));
  }

  generateOtpForForgotPassword(schoolDomain: string, emailId: string) {
    return this.http
      .post<GenerateOtpForForgotPasswordResponse>(`${this.url}${this.generateOtpForForgotPasswordEndpoint}`, { schoolDomain, emailId })
      .pipe(map((res) => res));
  }

  verifyOtpForForgotPassword(schoolDomain: string, emailId: string, otp: string) {
    return this.http
      .post<VerifyOtpForForgotPasswordResponse>(`${this.url}${this.verifyOtpForForgotPasswordEndpoint}`, { schoolDomain, emailId, otp })
      .pipe(map((res) => res));
  }

  resetPasswordForStaff(schoolDomain: string, emailId: string, token: string, password: string) {
    return this.http
      .put<ResetPasswordForStaffResponse>(`${this.url}${this.resetPasswordForStaffEndpoint}`, { schoolDomain, emailId, token, password })
      .pipe(map((res) => res));
  }
}
